import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Truck, Check, X, Clock3, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtDT = (s) => (s ? new Date(s).toLocaleString('en-ZA') : '—');

const badge = (s) => {
  if (s === 'approved') return <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
  if (s === 'declined') return <Badge className="bg-rose-600"><XCircle className="h-3 w-3 mr-1" />Declined</Badge>;
  return <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50"><Clock3 className="h-3 w-3 mr-1" />Pending</Badge>;
};

export default function VehicleChangeRequestsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // { req, decision, note }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vehicle-change-requests`);
      setRows(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/vehicle-change-requests/${reviewing.req.id}/approve`, {
        decision: reviewing.decision,
        note: reviewing.note,
      });
      toast.success(reviewing.decision === 'approve' ? 'Approved · driver vehicle updated' : 'Declined');
      setReviewing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const pending = rows.filter((r) => r.status === 'pending');
  const decided = rows.filter((r) => r.status !== 'pending');

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6 text-indigo-600" /> Vehicle-Change Requests
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Drivers asking to swap to a different vehicle. On approval, their `assigned_vehicle_registration` updates and Fleet Maintenance is notified.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-amber-500" /> Pending ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="text-center py-6 text-muted-foreground">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">No pending requests 🎉</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Driver</TableHead>
                    <TableHead>Current</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Requested at</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((r) => (
                    <TableRow key={r.id} data-testid={`vc-pending-${r.id}`}>
                      <TableCell className="font-medium">{r.driver_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.current_registration || <span className="text-slate-400">none</span>}</TableCell>
                      <TableCell className="font-mono text-xs text-indigo-700 font-semibold">🚚 {r.new_registration}</TableCell>
                      <TableCell className="text-xs max-w-xs">{r.reason}</TableCell>
                      <TableCell className="text-xs">{fmtDT(r.created_at)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => setReviewing({ req: r, decision: 'approve', note: '' })}
                                data-testid={`vc-approve-${r.id}`}>
                          <Check className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="border-rose-400 text-rose-700 hover:bg-rose-50"
                                onClick={() => setReviewing({ req: r, decision: 'decline', note: '' })}
                                data-testid={`vc-decline-${r.id}`}>
                          <X className="h-3 w-3 mr-1" /> Decline
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">History ({decided.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {decided.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">Nothing yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Driver</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reviewed by</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decided.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.driver_name}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.current_registration || 'none'} <span className="text-slate-400">→</span>{' '}
                        <span className="text-indigo-700 font-semibold">{r.new_registration}</span>
                      </TableCell>
                      <TableCell>{badge(r.status)}</TableCell>
                      <TableCell className="text-xs">{r.reviewed_by || '—'}</TableCell>
                      <TableCell className="text-xs italic text-muted-foreground max-w-xs truncate">{r.review_note || '—'}</TableCell>
                      <TableCell className="text-xs">{fmtDT(r.reviewed_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
          <DialogContent data-testid="vc-review-dialog">
            <DialogHeader>
              <DialogTitle>{reviewing?.decision === 'approve' ? 'Approve' : 'Decline'} vehicle change</DialogTitle>
              <DialogDescription>
                <b>{reviewing?.req?.driver_name}</b> · {reviewing?.req?.current_registration || 'none'} → <b>{reviewing?.req?.new_registration}</b>
                {reviewing?.decision === 'approve' && ' — driver\'s assigned vehicle will update immediately.'}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={3}
              value={reviewing?.note || ''}
              onChange={(e) => setReviewing({ ...reviewing, note: e.target.value })}
              placeholder="Optional note (visible to the driver)"
              data-testid="vc-note"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
              <Button onClick={submit} disabled={saving}
                      className={reviewing?.decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
                      data-testid="vc-confirm">
                {saving ? 'Saving…' : reviewing?.decision === 'approve' ? 'Approve' : 'Decline'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
